import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import cx from "classnames";
import { inView } from "motion";
import { GalleryImage } from "./GalleryImage";

export type Image = {
  src: string;
  alt?: string;
  limitHeight?: boolean;
};

export function HoverGallery({ className, images }: { className?: string; images: Image[] }) {
  const [showGuide, setShowGuide] = useState(false);
  const [currImage, setCurrImage] = useState<Image | undefined>(images[0]); // Default to first image

  // Remove the first image from the gallery (as it is the default image)
  const trimmedImages = images.slice(1);

  // Render a hover overlay for each image in the gallery
  const overlays = trimmedImages.map((image, index) => (
    <div
      key={index}
      className={cx("h-full grow flex items-center justify-center inset-shadow-sm/15 transition-opacity duration-500 ease-in-out", {
        "opacity-0": !showGuide,
      })}
      style={{
        backgroundColor: `rgba(0, 0, 0, ${0.2 - 0.02 * index})`,
        backdropFilter: "blur(1px)",
      }}
      onMouseEnter={() => setCurrImage(trimmedImages[index])}
    >
      <p className='text-xl text-neutral-100 drop-shadow-xs/60'>{index + 2}</p>
    </div>
  ));

  // Display the hover guide in the beginning for 3 seconds
  const hoverGuideRef = useRef(null);

  useEffect(() => {
    if (!hoverGuideRef.current) return;
    const stop = inView(hoverGuideRef.current, () => {
      setShowGuide(true);

      setTimeout(() => {
        setShowGuide(false);
        stop(); // only trigger once
      }, 5000);
    });
    return stop;
  }, [hoverGuideRef]);

  return (
    <div className={twMerge("w-full relative", className)}>
      <div className='flex overflow-hidden justify-center rounded-t-xl'>
        {/* Three images to fill the width */}
        {currImage && (
          <>
            <GalleryImage src={currImage.src} alt={currImage.alt} limitHeight={currImage.limitHeight} blur />
            <GalleryImage src={currImage.src} alt={currImage.alt} limitHeight={currImage.limitHeight} />
            <GalleryImage src={currImage.src} alt={currImage.alt} limitHeight={currImage.limitHeight} blur />
          </>
        )}
      </div>
      {images.length > 1 && (
        <div
          className='absolute flex h-full w-full top-0 left-0 rounded-t-xl overflow-hidden z-20'
          onMouseEnter={() => setShowGuide(false)}
          onMouseLeave={() => setCurrImage(images[0])}
          ref={hoverGuideRef}
        >
          {overlays}
        </div>
      )}
    </div>
  );
}
